import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const StructuredData: React.FC = () => {
  const { t, i18n } = useTranslation();
  
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "TamerinTECH AI Art",
    "description": "Unique AI-generated artworks with human soul. Custom art prints on canvas and premium materials.",
    "url": "https://tamerin.tech",
    "logo": "https://tamerin.tech/logo.png",
    "image": "https://tamerin.tech/images/gallery/default/snail-wain-at-the-ford.png",
    "foundingDate": "2024",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "DE"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+49-1590-7614478",
      "email": "info@tamerin.tech",
      "contactType": "customer service",
      "availableLanguage": ["German", "English", "Spanish", "French"]
    },
    "sameAs": [
      "https://wa.me/4915907614478"
    ],
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Product",
        "name": "Custom AI Art Prints",
        "description": "Unique AI-generated artworks printed on high-quality canvas and Alu-Dibond materials"
      }
    }
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": t('faq.questions.whatIsAiArt.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.whatIsAiArt.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.howIsArtCreated.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.howIsArtCreated.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.printQuality.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.printQuality.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.customOrders.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.customOrders.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.deliveryTime.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.deliveryTime.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.printingSizes.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.printingSizes.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.artStyles.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.artStyles.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.orderProcess.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.orderProcess.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.uniqueness.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.uniqueness.answer')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.questions.collaboration.question'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.questions.collaboration.answer')
        }
      }
    ]
  };

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Custom AI Art Creation and Printing",
    "description": "Professional AI art generation and high-quality printing services on canvas and Alu-Dibond materials",
    "provider": {
      "@type": "Organization",
      "name": "TamerinTECH AI Art"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Germany"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AI Art Prints",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Canvas Print 20x30cm",
            "description": "High-quality AI art print on canvas material"
          },
          "price": "30",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Canvas Print 40x60cm",
            "description": "Premium AI art print on canvas material"
          },
          "price": "63",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Alu-Dibond Print A1",
            "description": "Modern AI art print on durable Alu-Dibond material"
          },
          "price": "140",
          "priceCurrency": "EUR"
        }
      ]
    }
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://tamerin.tech"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "AI Art Gallery",
        "item": "https://tamerin.tech#preview-gallery"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "FAQ",
        "item": "https://tamerin.tech#faq"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Contact",
        "item": "https://tamerin.tech#contact"
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary SEO Meta Tags */}
      <title>{`${t('hero.title')} - ${t('hero.subtitle')} | 2025`}</title>
      <meta name="description" content={`${t('hero.description')} Custom AI art prints, canvas printing, professional quality. Contact us for unique AI-generated artworks 2025.`} />
      <meta name="keywords" content="AI art, artificial intelligence art, custom art prints, canvas prints, AI generated artwork, unique art, surreal art, TamerinTECH, 2025, Germany" />
      <meta name="author" content="TamerinTECH AI Art" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content={i18n.language} />
      
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:title" content={`${t('hero.title')} - ${t('hero.subtitle')}`} />
      <meta property="og:description" content={t('hero.description')} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://tamerin.tech" />
      <meta property="og:image" content="https://tamerin.tech/images/gallery/default/snail-wain-at-the-ford.png" />
      <meta property="og:image:alt" content="Unique AI-generated artwork example from TamerinTECH" />
      <meta property="og:site_name" content="TamerinTECH AI Art" />
      <meta property="og:locale" content={i18n.language === 'de' ? 'de_DE' : i18n.language === 'es' ? 'es_ES' : i18n.language === 'fr' ? 'fr_FR' : 'en_US'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${t('hero.title')} - ${t('hero.subtitle')}`} />
      <meta name="twitter:description" content={t('hero.description')} />
      <meta name="twitter:image" content="https://tamerin.tech/images/gallery/default/snail-wain-at-the-ford.png" />
      
      {/* Additional AEO Optimization Meta Tags */}
      <meta name="theme-color" content="#b9a06e" />
      <meta name="apple-mobile-web-app-title" content="TamerinTECH AI Art" />
      <meta name="application-name" content="TamerinTECH AI Art" />
      
      {/* Structured Data JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
    </Helmet>
  );
};

export default StructuredData;